<template>
  <div>
    <v-row class="mt-5 ml-4">
      <v-col>
        <v-row>
          <v-col>
            <v-btn outlined v-on:click="importFileDialog = true" small>
              Import ECI files
            </v-btn>
            <v-dialog
              v-model="confirmDeleteSelectedDialog"
              :persistent="deleting"
              width="500"
            >
              <template v-slot:activator="{}">
                <v-btn
                  color="orange lighten-2"
                  class="ml-1 white--text"
                  small
                  outlined
                  @click="confirmDeleteSelectedDialog = true"
                >
                  Delete currently matching
                </v-btn>
              </template>

              <v-card :loading="deleting">
                <v-card-title class="cardheader">
                  <span class="headline black--text">Warning</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    small
                    class="m-0"
                    :disabled="deleting"
                    @click="confirmDeleteSelectedDialog = false"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="mt-5">
                  <span class="dialogtext">
                    Are you sure you want to
                    <strong>delete ALL measures</strong> matching the
                    <strong>current criteria</strong> ?
                  </span>
                </v-card-text>
                <v-card-actions class="mt-n7">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    v-on:click="deleteSelected()"
                    :disabled="deleting"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="confirmDeleteAllDialog"
              :persistent="deleting"
              width="500"
            >
              <template v-slot:activator="{}">
                <v-btn
                  color="red lighten-1"
                  class="ml-1 white--text"
                  small
                  outlined
                  @click="confirmDeleteAllDialog = true"
                >
                  Delete all
                </v-btn>
              </template>

              <v-card :loading="deleting">
                <v-card-title class="cardheader">
                  <span class="headline black--text">Warning</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    small
                    class="m-0"
                    :disabled="deleting"
                    @click="confirmDeleteAllDialog = false"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="mt-5">
                  <span class="dialogtext">
                    Are you sure you want to
                    <strong>delete ALL measures</strong> in the database ?</span
                  >
                </v-card-text>
                <v-card-actions class="mt-n7">
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    text
                    v-on:click="deleteAll()"
                    :disabled="deleting"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
        <v-dialog
          scrollable
          width="550"
          v-model="importFileDialog"
          :persistent="importing"
        >
          <import-eci-file
            :root="this"
            v-on:update:importing="importing = $event"
          />
        </v-dialog>
        <exo-form-alert type="info" class="mt-3">
          The import menu is generic to all missions. All selected measurements
          will be saved and persistent in database. They will be retrieved
          according to the NORAD ID selected in space object.
        </exo-form-alert>
      </v-col>
    </v-row>
    <v-row class="mt-5 ml-4 mr-4">
      <v-col>
        <v-text-field
          :label="'NORAD ID'"
          :value="noradID"
          @input="updateNoradID"
          :outlined="true"
          :dense="true"
          :rules="noradIDRules"
        />
      </v-col>
      <v-col>
        <exoDatetimePicker
          outlined
          class="mt-3"
          dense
          :label="'Display measures after'"
          v-model="afterDate"
          @input="updateAfterDate"
        />
      </v-col>
      <v-col>
        <exoDatetimePicker
          outlined
          class="mt-3"
          dense
          :label="'Display measures before'"
          v-model="beforeDate"
          @input="updateBeforeDate"
        />
      </v-col>
    </v-row>
    <SimulationIdSelector
      :updating.sync="updateSimulationIds"
      :typeToDisplayProp.sync="typeToDisplay"
      :selectedIdsProp.sync="selectedIds"
    ></SimulationIdSelector>
    <v-row class="ml-4 mr-4">
      <v-col>
        <v-data-table
          :headers="tableFields"
          :items="displayedMeasurements"
          :options.sync="options"
          :loading="loading || initializing"
          :server-items-length="totalNbMeasurements"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [5, 10, 15, 30, 100]
          }"
          class="elevation-1"
        >
          <template v-slot:[`item.delete`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  class="mx-0"
                  @click.stop="deleteOne(item.id)"
                  v-on="on"
                >
                  <v-icon color="pink">delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ImportEphemeridesFile from './ImportEphemeridesFile'
import exoDatetimePicker from '@/components/Forms/dateTimePicker.vue'
import SimulationIdSelector from './SimulationIdSelector'
import errorMixin from '@/mixins/logHandler'

export default {
  name: 'ManeuverAnalysisData',

  components: {
    'import-eci-file': ImportEphemeridesFile,
    exoDatetimePicker,
    SimulationIdSelector
  },

  mixins: [errorMixin],

  watch: {
    options: {
      async handler() {
        await this.update()
      },
      deep: true
    },
    importFileDialog(newValue) {
      if (newValue === false) {
        this.update()
      }
    },
    simulationIds(newValue) {
      this.updateCurrentMeasures()
    }
  },

  async mounted() {
    this.$root.$on('open-dialog', this.openDialogHandler)
    this.init()
  },

  beforeDestroy() {
    this.$root.$off('open-dialog', this.openDialogHandler)
  },

  computed: {
    ...mapGetters({ currentUser: 'currentUser', missions: 'missions' }),
    missionIdToName() {
      return this.missions.reduce((partialResult, mission) => {
        return { ...partialResult, [mission.id]: mission.name }
      }, {})
    },
    displayedMeasurements() {
      return this.initializing ? [] : this.measurements
    },
    simulationIdsList() {
      let simulationIdsList = []
      let selectedIds = this.selectedIds || []
      switch (this.typeToDisplay) {
        case 'GENERATED':
          simulationIdsList = selectedIds.slice()
          break
        case 'NONGENERATED':
          simulationIdsList.push('null')
          break
        default:
          simulationIdsList.push('null')
          simulationIdsList = simulationIdsList.concat(selectedIds.slice())
      }
      return simulationIdsList
    },

    simulationIds() {
      return this.simulationIdsList.reduce(
        (currentIdsListString, id) => `${id},${currentIdsListString}`,
        ''
      )
    },
    getPageParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const params = { page, itemsPerPage }

      if (sortBy.length === 1) {
        params.sortBy = sortBy[0]
        params.sortDesc = sortDesc[0]
      }

      if (this.beforeDate != null) {
        params.endDate = this.beforeDate
      }

      if (this.afterDate != null) {
        params.startDate = this.afterDate
      }

      if (this.noradID != null && this.noradID != '') {
        params.noradID = this.noradID
      }

      if (this.simulationIds != null) {
        params.simulationIds = this.simulationIds
      }

      return params
    }
  },

  data() {
    return {
      importFileDialog: false,
      confirmDeleteAllDialog: false,
      confirmDeleteSelectedDialog: false,
      measurements: [],
      tableFields: [
        { value: 'noradID', text: 'NORAD ID' },
        {
          value: 'startDate',
          text: 'Start date'
        },
        { text: 'Generated ?', value: 'isGenerated', sortable: false },
        {
          text: 'Generated by',
          value: 'simulationMissionName',
          sortable: false
        },
        { text: '', value: 'delete', sortable: false }
      ],
      options: {},
      loading: false,
      totalNbMeasurements: 0,
      beforeDate: null,
      afterDate: null,
      noradID: null,
      noradIDRules: [
        (value) => {
          const pattern = /^(\d{5})?$/
          return value == null || pattern.test(value) || 'Invalid NORAD ID.'
        }
      ],
      importing: false,
      deleting: false,
      initializing: true,
      updateSimulationIds: false,
      typeToDisplay: 'ALL',
      selectedIds: null
    }
  },
  methods: {
    ...mapActions(['loadMissions']),
    async init() {
      this.initializing = true
      await this.loadMissions(this.$root)
      await this.update()
      this.initializing = false
    },
    getNameFromMissionId(id) {
      if (id === 'null') {
        return ''
      }
      return this.missionIdToName[id] || `Deleted mission(${id})`
    },
    deleteOne(id) {
      this.$http
        .delete(`/users/${this.currentUser.id}/spmeasurements/${id}`)
        .then(() => {
          this.update()
        })
        .catch((e) => {
          this.displayMessage(
            'Error deleting measurement' + id + '. ',
            e,
            'notification',
            'error'
          )
        })
    },
    deleteAll() {
      this.deleting = true
      this.$http
        .delete(`/users/${this.currentUser.id}/spmeasurements`)
        .then(() => {
          this.update()
        })
        .catch((e) => {
          this.displayMessage(
            'Error while deleting all measurements.',
            e,
            'notification',
            'error'
          )
        })
        .finally(() => {
          this.deleting = false
          this.confirmDeleteAllDialog = false
        })
    },
    deleteSelected() {
      this.deleting = true
      const { startDate, endDate, noradID } = this.getPageParams
      this.$http
        .delete(`/users/${this.currentUser.id}/spmeasurements/fromparams`, {
          data: {
            startDate,
            endDate,
            noradID,
            simulationIds: this.simulationIdsList
          }
        })
        .then(() => {
          this.update()
        })
        .catch((e) => {
          this.displayMessage(
            'Error while deleting all measurements.',
            e,
            'notification',
            'error'
          )
        })
        .finally(() => {
          this.deleting = false
          this.confirmDeleteSelectedDialog = false
        })
    },
    openDialogHandler() {
      this.importFileDialog = true
    },
    async update() {
      this.updateSimulationIds = true
      await this.updateCurrentMeasures()
    },
    getPage() {
      const params = this.getPageParams
      const measurements = this.$http
        .get(`/users/${this.currentUser.id}/spmeasurements/getpage`, {
          params
        })
        .then((response) => response.data)
        .catch((e) => {
          this.displayMessage(
            'Error while getting measurements.',
            e,
            'notification',
            'error'
          )
          return null
        })

      return measurements
    },
    async updateCurrentMeasures() {
      this.loading = true
      const pageResult = await this.getPage()
      if (pageResult != null) {
        this.measurements = pageResult.measurementsList
        this.totalNbMeasurements = pageResult.totalNbMeasurements
        for (const measurement of this.measurements) {
          measurement.startDate = measurement.startDate.substring(0, 24)
          measurement.isGenerated =
            measurement.simulationId !== 'null' ? 'YES' : 'NO'
          measurement.simulationMissionName = this.getNameFromMissionId(
            measurement.simulationId
          )
        }
        if (
          pageResult.measurementsList.length === 0 &&
          pageResult.totalNbMeasurements !== 0
        ) {
          this.options.page = 1
        }
      } else {
        this.measurements = []
        this.totalNbMeasurements = 0
      }
      this.loading = false
    },
    async updateBeforeDate(value) {
      this.beforeDate = value
      await this.update()
    },
    async updateAfterDate(value) {
      this.afterDate = value
      await this.update()
    },
    async updateNoradID(value) {
      let valid = this.noradIDRules
        .map((rule) =>
          typeof rule === 'function' ? rule(value) === true : rule === true
        )
        .reduce((a, b) => a && b)
      if (valid) {
        this.noradID = value
        await this.update()
      }
    }
  }
}
</script>

<style scoped>
.cardheader {
  background: #3be287;
}

.dialogtext {
  font-size: 120%;
}
</style>
